import { useEffect, useState } from 'react';
import { useScript } from 'usehooks-ts';

type UseScriptStatus = ReturnType<typeof useScript>;
const cachedScriptStatuses: Record<string, UseScriptStatus> = {};

function getStylesNode(src: string) {
	const node = document.querySelector(`link[href="${src}"]`) as HTMLLinkElement;
	const status =
		node === null || node === void 0
			? void 0
			: (node.getAttribute('data-status') as UseScriptStatus);

	return {
		node,
		status,
	};
}

// The useScript hook copied from 'usehooks-ts', but adjusted for styles tag
export const useStyle: typeof useScript = (src, options) => {
	const areOptionsMissing = options === null || options === void 0;
	const [status, setStatus] = useState<UseScriptStatus>(() => {
		let _a;

		if (!src || (areOptionsMissing ? void 0 : options.shouldPreventLoad)) {
			return 'idle';
		}
		if (typeof window === 'undefined') {
			return 'loading';
		}

		return (_a = cachedScriptStatuses[src]) !== null && _a !== void 0 ? _a : 'loading';
	});

	const shouldPreventLoadFlag = areOptionsMissing ? void 0 : options.shouldPreventLoad;
	const shouldRemoveOnUnmount = areOptionsMissing ? void 0 : options.removeOnUnmount;

	useEffect(() => {
		let _a, _b;
		if (!src || shouldPreventLoadFlag) {
			return;
		}
		const cachedScriptStatus = cachedScriptStatuses[src];
		if (cachedScriptStatus === 'ready' || cachedScriptStatus === 'error') {
			setStatus(cachedScriptStatus);

			return;
		}
		const stylesheet = getStylesNode(src);
		let stylesheetNode = stylesheet.node;

		if (!stylesheetNode) {
			stylesheetNode = document.createElement('link');
			stylesheetNode.href = src;
			stylesheetNode.rel = 'stylesheet';
			stylesheetNode.setAttribute('data-status', 'loading');

			document.body.appendChild(stylesheetNode);
			const setAttributeFromEvent = (event: HTMLElementEventMap['load' | 'error']) => {
				const scriptStatus = event.type === 'load' ? 'ready' : 'error';
				stylesheetNode === null || stylesheetNode === void 0
					? void 0
					: stylesheetNode.setAttribute('data-status', scriptStatus);
			};
			stylesheetNode.addEventListener('load', setAttributeFromEvent);
			stylesheetNode.addEventListener('error', setAttributeFromEvent);
		} else {
			setStatus(
				(_b = (_a = stylesheet.status) !== null && _a !== void 0 ? _a : cachedScriptStatus) !==
					null && _b !== void 0
					? _b
					: 'loading',
			);
		}
		const setStateFromEvent = (event: HTMLElementEventMap['load' | 'error']) => {
			const newStatus = event.type === 'load' ? 'ready' : 'error';
			setStatus(newStatus);
			cachedScriptStatuses[src] = newStatus;
		};
		stylesheetNode.addEventListener('load', setStateFromEvent);
		stylesheetNode.addEventListener('error', setStateFromEvent);

		return () => {
			if (stylesheetNode) {
				stylesheetNode.removeEventListener('load', setStateFromEvent);
				stylesheetNode.removeEventListener('error', setStateFromEvent);
			}

			if (stylesheetNode && shouldRemoveOnUnmount) {
				stylesheetNode.remove();
			}
		};
	}, [src, shouldPreventLoadFlag, shouldRemoveOnUnmount]);

	return status;
};
